/**
 * MyAnalyticsPanel — compact stats table for a creator's own posts.
 * Shown on the "Mine" tab of the Publish feed. Basic, at-a-glance
 * numbers per post (views, likes, dislikes, comments, bookmarks, shares).
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Eye, Heart, ThumbsDown, ChatText, BookmarkSimple, ShareNetwork, ChartLine,
} from "@phosphor-icons/react";

const TYPE_LABEL = {
  story: "Story", article: "Article", advertisement: "Ad",
};

const STATUS_STYLE = {
  published: "bg-emerald-100 text-emerald-800 border-emerald-200",
  hidden:    "bg-amber-100 text-amber-800 border-amber-200",
  deleted:   "bg-rose-100 text-rose-800 border-rose-200",
  draft:     "bg-slate-100 text-slate-700 border-slate-200",
};

export function MyAnalyticsPanel({ posts = [] }) {
  const totals = useMemo(() => {
    const t = { views: 0, likes: 0, dislikes: 0, comments: 0, bookmarks: 0, shares: 0 };
    for (const p of posts) {
      t.views += p.views_count || 0;
      t.likes += p.likes_count || 0;
      t.dislikes += p.dislikes_count || 0;
      t.comments += p.comments_count || 0;
      t.bookmarks += p.bookmarks_count || 0;
      t.shares += p.shares_count || 0;
    }
    return t;
  }, [posts]);

  if (!posts.length) return null;

  return (
    <Card className="mb-4" data-testid="my-analytics-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ChartLine size={16} weight="duotone" /> Your analytics
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Basic performance snapshot for each of your posts.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Totals row */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
          <Stat icon={Eye}            label="Views"     value={totals.views} />
          <Stat icon={Heart}          label="Likes"     value={totals.likes} />
          <Stat icon={ThumbsDown}     label="Dislikes"  value={totals.dislikes} />
          <Stat icon={ChatText}       label="Comments"  value={totals.comments} />
          <Stat icon={BookmarkSimple} label="Bookmarks" value={totals.bookmarks} />
          <Stat icon={ShareNetwork}   label="Shares"    value={totals.shares} />
        </div>

        {/* Per-post table */}
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr className="border-b">
                <th className="text-left font-medium px-3 py-2">Post</th>
                <th className="text-right font-medium px-2 py-2"><Eye size={12} className="inline mr-1" />Views</th>
                <th className="text-right font-medium px-2 py-2"><Heart size={12} className="inline mr-1" />Likes</th>
                <th className="text-right font-medium px-2 py-2"><ThumbsDown size={12} className="inline mr-1" />Dislikes</th>
                <th className="text-right font-medium px-2 py-2"><ChatText size={12} className="inline mr-1" />Comments</th>
                <th className="text-right font-medium px-2 py-2"><BookmarkSimple size={12} className="inline mr-1" />Bookmarks</th>
                <th className="text-right font-medium px-2 py-2"><ShareNetwork size={12} className="inline mr-1" />Shares</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {posts.map(p => (
                <tr key={p.id} data-testid={`analytics-row-${p.id}`} className="hover:bg-muted/40">
                  <td className="px-3 py-2 min-w-[200px]">
                    <div className="flex flex-col gap-0.5">
                      <Link to={`/app/publish/${p.id}`}
                            className="text-sm font-medium text-foreground hover:text-primary truncate max-w-[280px]">
                        {p.title || `Untitled ${TYPE_LABEL[p.post_type] || "post"}`}
                      </Link>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className="text-[10px] uppercase">
                          {TYPE_LABEL[p.post_type] || p.post_type}
                        </Badge>
                        {p.status !== "published" && (
                          <Badge variant="outline" className={`text-[10px] uppercase ${STATUS_STYLE[p.status] || ""}`}>
                            {p.status}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">{p.views_count || 0}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{p.likes_count || 0}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{p.dislikes_count || 0}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{p.comments_count || 0}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{p.bookmarks_count || 0}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{p.shares_count || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-border rounded-lg p-2.5 bg-card">
      <div className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-muted-foreground">
        <Icon size={12} weight="duotone" /> {label}
      </div>
      <div className="text-xl font-bold tabular-nums text-foreground">
        {value.toLocaleString()}
      </div>
    </div>
  );
}
